import type { JSX, PropsWithChildren, Ref } from "react";
import { Children, forwardRef, memo, useState } from "react";
import type { RowData } from "@tanstack/react-table";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import classes from "./styles.module.scss";
import { ListBoxHeader } from "./listBoxHeader";

import type { IListBoxProps } from ".";

import { Box } from "@/components/atoms/box";
import { Table } from "@/components/organisms/table";
import { Chip } from "@components/atoms/chip";

const ListBoxComponent = forwardRef(function ListBoxComponent<
  TData extends RowData,
>(props: PropsWithChildren<IListBoxProps<TData>>, ref: Ref<HTMLDivElement>) {
  const {
    title,
    onDownload,
    onPrint,
    columnDef,
    dataJSON = [],
    children,
    iconName: IconName = "List",
    contentClassName,
    boxClassName,
    wrapperClassName,
    editProps,
    haveViewMore = true,
    isLoading,
    isFetching,
    isError,
    titlePostfix,
    ...tableProps
  } = props;

  const { t } = useTranslation("form");

  const [open, setOpen] = useState(false);

  return (
    <div ref={ref} className={clsx(classes.container, boxClassName)}>
      <Box
        px={"0"}
        py={"0"}
        radius={"md"}
        shadow
        className={clsx(classes.footer, classes.box, wrapperClassName)}
      >
        <ListBoxHeader
          {...{
            title,
            onPrint,
            onDownload,
            iconName: IconName,
            editProps,
            isFetching,
            titlePostfix,
          }}
        />
        {isError && (
          <div className={classes.errorContainer}>
            <Chip name={"no-detect"} value={t("errors.serverError")} />
          </div>
        )}
        {!isError &&
          isLoading &&
          (!columnDef && Children.count(children) !== 0 ? (
            <div className={clsx(classes.column)}>
              <div className={clsx(classes.row, classes.rowTable)}>
                <Skeleton count={1} />
              </div>
            </div>
          ) : (
            <div className={clsx(classes.column)}>
              <div className={clsx(classes.row)}>
                <Skeleton count={6} />
              </div>
              <div className={clsx(classes.row, classes.rowTable)}>
                <Skeleton count={6} />
              </div>
            </div>
          ))}
        {!isError && !isLoading && columnDef && (
          <>
            <Table
              tableClassName={classes.table}
              tableContainerClassName={clsx(classes["table-container"])}
              {...tableProps}
              columnDef={columnDef}
              dataJSON={!haveViewMore || open ? dataJSON : dataJSON.slice(0, 5)}
            />
            {dataJSON.length > 5 && haveViewMore && (
              <div
                className={clsx(classes.viewMoreRow)}
                onClick={() => {
                  setOpen((prevState) => !prevState);
                }}
              >
                {t(open ? "viewLess" : "viewMore")}
              </div>
            )}
            {/* <div className='border-bottom'></div> */}
          </>
        )}
        {!isError && !isLoading && Children.count(children) !== 0 && (
          <div
            className={clsx([classes["children-container"], contentClassName])}
          >
            {children}
          </div>
        )}
      </Box>
    </div>
  );
});

// Use a generic type with React.memo
export const ListBox = memo(ListBoxComponent) as <TData extends RowData>(
  props: PropsWithChildren<IListBoxProps<TData>> & {
    ref?: Ref<HTMLDivElement>;
  },
) => JSX.Element;
